import React, { useEffect, useState } from "react";
import { Platform } from "react-native";
import { FieldValues, useFormContext } from "react-hook-form";

import {
  VnAccordion,
  VnAccordionContent,
  VnAccordionItem,
  VnAccordionTrigger,
} from "@/components/VnAccordion";
import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { BaseCheckboxInput } from "@/components/VnCheckboxInput/BaseCheckboxInput";
import { VnFormLabel } from "@/components/VnForm";
import { VnSeparator } from "@/components/VnSeparator";
import { VnText } from "@/components/VnText/VnText";
import { VnTouchableOpacity } from "@/components/VnTouchableOpacity";
import { HouseholdMemberType } from "@/domain/householdMember";
import { I18nKeyType } from "@/i18n";
import { formatHouseholdMemberName } from "@/utils/householdMember";
import { cn } from "@/utils/tailwind";

type OptionType<T extends string = string> = {
  optionName: T;
  optionValue: { active: string; inactive: string };
  i18nKey: I18nKeyType;
};

type MemberAccordionSelectInputPropsType<T extends string = string> = {
  options: OptionType<T>[];
  members: HouseholdMemberType[];
  renderMemberInfo?: (props: {
    optionName: T;
    member: HouseholdMemberType;
  }) => React.ReactNode;
  renderMemberActions?: (props: {
    optionName: T;
    member: HouseholdMemberType;
  }) => React.ReactNode;
  onNoneOfTheAboveSelected?: (isSelected: boolean) => void;
};

export const MemberAccordionSelectInput = <T extends string = string>({
  options,
  members,
  renderMemberInfo,
  renderMemberActions,
  onNoneOfTheAboveSelected,
}: MemberAccordionSelectInputPropsType<T>) => {
  const form = useFormContext<FieldValues>();
  const { watch, setValue } = form;

  const [openAccordions, setOpenAccordions] = useState<T[]>([]);
  const [isNoneOfTheAbove, setIsNoneOfTheAbove] = useState(false);

  useEffect(() => {
    // Open accordions for options that already have active values
    members.forEach(member => {
      options.forEach(({ optionName, optionValue }) => {
        const existingValue = member[optionName as keyof HouseholdMemberType];
        if (existingValue && existingValue === optionValue.active) {
          setOpenAccordions(prev => [...prev, optionName]);
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleNoneOfTheAboveSection = (isSelected: boolean) => {
    if (isSelected) {
      // set all options to inactive
      members.forEach(member => {
        options.forEach(({ optionName, optionValue }) => {
          const fieldKey = `${member.id}.${optionName}`;
          setValue(fieldKey, optionValue.inactive, { shouldDirty: true });
        });
      });
      setOpenAccordions([]);
    } else {
      setOpenAccordions(prev => prev.filter(item => item !== "noneOfTheAbove"));
    }
    setIsNoneOfTheAbove(isSelected);
    onNoneOfTheAboveSelected?.(isSelected);
  };

  useEffect(() => {
    if (openAccordions.length) {
      setIsNoneOfTheAbove(false);
    }
  }, [openAccordions]);

  return (
    <VnAccordion
      type="multiple"
      value={openAccordions}
      onValueChange={value => setOpenAccordions(value as T[])}
      className="flex w-full">
      <VnBox className="gap-y-5">
        {options.map(({ optionName, optionValue, i18nKey }) => {
          const isChecked = openAccordions.includes(optionName);

          const onToggle = (isSelected: boolean) => {
            setOpenAccordions(prev =>
              isSelected
                ? [...prev, optionName]
                : prev.filter(item => item !== optionName),
            );

            // When toggling off, reset all members to inactive for this option
            if (!isSelected) {
              members.forEach(member => {
                const fieldKey = `${member.id}.${optionName}`;
                setValue(fieldKey, optionValue.inactive, { shouldDirty: true });
              });
            }
          };

          return (
            <VnCard key={optionName} className="gap-4 rounded-md p-4">
              <VnAccordionItem value={optionName}>
                <VnAccordionTrigger
                  showChevronIcon={false}
                  className="w-full gap-1.5 py-0">
                  <VnBox className="flex min-w-0 flex-1 flex-row gap-x-2.5">
                    <BaseCheckboxInput
                      checked={isChecked}
                      onCheckedChange={onToggle}
                    />
                    <VnText variant="lg" i18nKey={i18nKey} className="shrink" />
                  </VnBox>
                </VnAccordionTrigger>
                <VnAccordionContent className="pb-0">
                  <VnBox className="bg-background mt-4 flex flex-col gap-2 rounded-sm p-4">
                    <VnText i18nKey="common:whoApplies" variant="mdMedium" />
                    <VnBox className="mt-2 flex flex-col gap-2">
                      {members.map(member => {
                        const fieldKey = `${member.id}.${optionName}`;
                        const currentStatus = watch(fieldKey) as string | null;
                        const isSelected = currentStatus === optionValue.active;
                        const toggleMemberStatus = () => {
                          setValue(
                            fieldKey,
                            isSelected
                              ? optionValue.inactive
                              : optionValue.active,
                            {
                              shouldDirty: true,
                            },
                          );
                        };
                        return (
                          <VnBox
                            key={member.id}
                            className="flex min-h-8 flex-row items-start justify-between gap-x-2.5">
                            <VnTouchableOpacity
                              onPress={toggleMemberStatus}
                              shouldDebounce={false}
                              className={cn(
                                "flex min-w-0 flex-1 flex-row items-start gap-x-2.5 rounded-md",
                                Platform.select({
                                  web: "cursor-pointer",
                                }),
                              )}>
                              <VnBox className="pt-0.5">
                                <BaseCheckboxInput
                                  checked={isSelected}
                                  onCheckedChange={toggleMemberStatus}
                                  className="bg-card web:pointer-events-none"
                                />
                              </VnBox>
                              <VnBox className="flex min-w-0 shrink flex-col">
                                <VnFormLabel variant="md">
                                  {formatHouseholdMemberName(member)}
                                </VnFormLabel>
                                {isSelected &&
                                  renderMemberInfo?.({
                                    optionName,
                                    member,
                                  })}
                              </VnBox>
                            </VnTouchableOpacity>
                            {isSelected &&
                              renderMemberActions?.({
                                optionName,
                                member,
                              })}
                          </VnBox>
                        );
                      })}
                    </VnBox>
                  </VnBox>
                </VnAccordionContent>
              </VnAccordionItem>
            </VnCard>
          );
        })}

        <VnSeparator />
        {/* Option: None of the above */}
        <VnCard className="gap-4 rounded-md border-t p-4">
          <VnAccordionItem value="noneOfTheAbove">
            <VnAccordionTrigger
              showChevronIcon={false}
              className="w-full gap-1.5 py-0">
              <VnBox className="flex min-w-0 flex-1 flex-row gap-x-2.5">
                <BaseCheckboxInput
                  checked={isNoneOfTheAbove}
                  onCheckedChange={toggleNoneOfTheAboveSection}
                />
                <VnText
                  variant="lg"
                  i18nKey="common:noneOfTheAbove"
                  className="w-full"
                  onPress={() => toggleNoneOfTheAboveSection(!isNoneOfTheAbove)}
                />
              </VnBox>
            </VnAccordionTrigger>
          </VnAccordionItem>
        </VnCard>
      </VnBox>
    </VnAccordion>
  );
};
